import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import axios from "axios";
interface User {
  id: string;
  email: string;
  username: string;
}

interface RegisterFormValues {
  fullName: string;
  username: string;
  phoneNumber: string;
  password: string;
  plan: string; // The new field for the selected plan
}
type FormValues = {
  email: string;
  full_name: string;
  profession: string;
  phone: string;
  practice_size: string;
  interest: string;
  problem: string;
};
interface SubscriptionInfo {
  active: boolean;
  status: string;
  clinic_name: string | null;
  plan_name: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  subscriptionInfo: SubscriptionInfo | null;
  leads: (payload: any) => Promise<{
    message: string;
    id: number;
    referral_code: string;
    referred_by: string | null;
    free_months_earned: number;
  }>;
  register: (payload: RegisterFormValues) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {
    throw new Error("AuthProvider not initialized");
  },
  logout: async () => {
    throw new Error("AuthProvider not initialized");
  },
  subscriptionInfo: null,
  leads: async () => {
    throw new Error("AuthProvider not initialized");
  },
  register: async () => {
    throw new Error("AuthProvider not initialized");
  },
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfo | null>(null);
  axios.defaults.withCredentials = true;
  const apiUrl = process.env.REACT_APP_API_URL || "/api";

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/auth/check/`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error();
      const data = await response.json();
      setUser(data.user);
      setSubscriptionInfo(data.subscription_info);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${apiUrl}/auth/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Login failed");
      }

      await checkAuthStatus();
    } catch (error) {
      throw error;
    }
  };

  const register = async (payload: RegisterFormValues) => {
    try {
      const response = await fetch(`${apiUrl}/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          status: response.status,
          data: errorData,
        };
      }
    } catch (error) {
      throw error;
    }
  };
  const leads = async (payload: any) => {
    try {
      const response = await fetch(`${apiUrl}/leads/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Registration failed");
      }

      // Parse and return the response data
      const data = await response.json();
      return data; // This is crucial!
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${apiUrl}/auth/logout/`, {
        method: "POST",
        credentials: "include",
      });
      setUser(null);
      setSubscriptionInfo(null);
    } catch (error) {}
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        subscriptionInfo,
        register,
        leads,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
